import React, { useCallback, useState } from 'react';
import { Sequence, Video, AbsoluteFill, Img } from 'remotion';
import { LayerSegment } from '../../types/layers';

interface VideoSegmentProps {
  segment: LayerSegment;
}

export const VideoSegment: React.FC<VideoSegmentProps> = ({ segment }) => {
  const [hasError, setHasError] = useState(false);

  const handleVideoError = useCallback((_error: Error) => {
    setHasError(true);
  }, []);

  const videoSeekFrame = segment.videoStartFrame || 0;

  const boxShadow = segment.shape.shadow
    ? `${segment.shape.shadow.offsetX}px ${segment.shape.shadow.offsetY}px ${segment.shape.shadow.blur}px ${segment.shape.shadow.color}`
    : undefined;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: segment.x,
    top: segment.y,
    width: segment.width,
    height: segment.height,
    overflow: 'hidden',
    borderRadius: segment.shape.borderRadius,
    ...(segment.shape.clipPath && { clipPath: segment.shape.clipPath }),
    ...(boxShadow && { boxShadow }),
  };

  const mediaStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    ...(segment.isMirrored && { transform: 'scaleX(-1)' }),
  };

  if (segment.isAvatar && segment.avatarUrl) {
    return (
      <Sequence
        from={segment.startFrame}
        durationInFrames={segment.durationFrames}
      >
        <AbsoluteFill>
          <div style={containerStyle}>
            <Img
              src={segment.avatarUrl}
              style={mediaStyle}
            />
          </div>
        </AbsoluteFill>
      </Sequence>
    );
  }

  if (segment.source.status === 'error' || segment.source.status === 'loading' || hasError) {
    return (
      <Sequence
        from={segment.startFrame}
        durationInFrames={segment.durationFrames}
      >
        <div
          style={{
            ...containerStyle,
            backgroundColor: '#1a1a1a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {segment.source.status === 'loading' && !hasError && (
            <div style={{ color: '#666', fontSize: 14 }}>Loading...</div>
          )}
          {(segment.source.status === 'error' || hasError) && (
            <div style={{ color: '#ff4444', fontSize: 12, textAlign: 'center', padding: 8 }}>
              Video unavailable
            </div>
          )}
        </div>
      </Sequence>
    );
  }

  return (
    <Sequence
      from={segment.startFrame}
      durationInFrames={segment.durationFrames}
    >
      <AbsoluteFill>
        <div style={containerStyle}>
          <Video
            src={segment.source.value}
            style={mediaStyle}
            volume={segment.volume}
            muted={segment.volume === 0}
            startFrom={videoSeekFrame}
            pauseWhenBuffering
            onError={handleVideoError}
          />
        </div>
      </AbsoluteFill>
    </Sequence>
  );
};
